import React, { useState, useEffect, useCallback } from 'react';
import { manualDownloadUrl, openManualDownloadUrl } from '../utils/updaterUi';
import { fetchConnectorLatestPublishedVersion } from '../utils/connectorLatestVersion';

const GOLD = '#C9A84C';
const DISMISSED_KEY = 'maestro_update_banner_dismissed';

function parseVersion(v) {
  if (!v) return null;
  const stripped = v.replace(/^v/i, '');
  const parts = stripped.split('.').map(Number);
  if (parts.some(isNaN)) return null;
  return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0, raw: stripped };
}

function isOutdated(current, latest) {
  if (!current || !latest) return false;
  if (latest.major > current.major) return true;
  if (latest.major === current.major && latest.minor > current.minor) return true;
  if (latest.major === current.major && latest.minor === current.minor && latest.patch > current.patch) return true;
  return false;
}

export default function ConnectorUpdateBanner() {
  const [latestVersion, setLatestVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const isTauri = typeof window !== 'undefined' &&
    ('__TAURI__' in window || '__MAESTRO_CONNECTOR__' in window);

  const connectorVersion = (typeof window !== 'undefined' && window.__MAESTRO_CONNECTOR__?.version) || null;

  useEffect(() => {
    if (!isTauri) return;

    const stored = sessionStorage.getItem(DISMISSED_KEY);
    if (stored === connectorVersion) {
      setDismissed(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const version = await fetchConnectorLatestPublishedVersion();
        if (!cancelled && version) {
          setLatestVersion(version);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [isTauri, connectorVersion]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISSED_KEY, connectorVersion || ''); } catch {}
  }, [connectorVersion]);

  if (!isTauri || dismissed) return null;

  const current = parseVersion(connectorVersion);
  const latest = parseVersion(latestVersion);
  if (!current || !latest || !isOutdated(current, latest)) return null;

  const downloadUrl = manualDownloadUrl(latest.raw);

  return (
    <div style={{
      background: `linear-gradient(135deg, ${GOLD}18, ${GOLD}10)`,
      borderBottom: `1px solid ${GOLD}40`,
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      position: 'relative',
      zIndex: 60,
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" stroke={GOLD} strokeWidth="2" />
        <path d="M12 7v6M12 16h.01" stroke={GOLD} strokeWidth="2" strokeLinecap="round" />
      </svg>

      <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }}>
        <span style={{ fontWeight: 700 }}>
          A new version of Maestro is available
        </span>
        <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>
          — v{latest.raw} (you have v{current.raw})
        </span>
        <span style={{ color: 'var(--text-tertiary)', marginLeft: 6 }}>
          Includes important playback fixes.
        </span>
      </div>

      <button
        type="button"
        onClick={() => openManualDownloadUrl(downloadUrl)}
        style={{
          flexShrink: 0,
          padding: '6px 14px',
          borderRadius: 8,
          border: 'none',
          background: GOLD,
          color: '#1a1a2e',
          fontSize: 12,
          fontWeight: 700,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
      >
        Download Now
      </button>

      <button
        onClick={handleDismiss}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          padding: 4,
          lineHeight: 1,
          fontSize: 18,
        }}
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
